<template>
  <AppSkeletonCardTable v-if="loading"></AppSkeletonCardTable>
  <Card v-else style="min-height: 333px">
    <template v-slot:title>
      <div class="p-grid p-jc-between">
        <div class="p-col p-lg-5">
          Bovinos no Serviço
          <router-link
            v-if="$route.params.id"
            :to="{
              name: 'bovine.list',
              query: { serviceId: $route.params.id },
            }"
            target="_blank"
          >
            <br class="hide mobile-show" />
            <span style="font-size: 1rem; font-weight: normal">
              (Listagem de Bovinos no Serviço)
            </span>
          </router-link>
        </div>
        <div class="p-col" style="text-align: right">
          <span
            style="display-inline-block"
            v-tooltip.top="
              !serviceId ? 'Salve o serviço para adicionar bovinos.' : ''
            "
          >
            <span>
              <router-link
                v-if="serviceId"
                :to="{
                  name: 'bovine.import',
                  query: {
                    serviceId: $route.params.id,
                    accessToken: this.customer?.accessToken,
                  },
                }"
                style="text-decoration: none"
              >
                <Button
                  label="Importar bovinos"
                  class="p-button-outlined p-mr-2 import-btn"
                />
              </router-link>
              <Button
                v-else
                disabled
                label="Importar bovinos"
                class="p-button-outlined p-mr-2 import-btn"
              />
            </span>
            <Button
              :disabled="!serviceId"
              label="Adicionar Bovinos"
              class="p-button-outlined"
              @click="selectorVisible = true"
            />
          </span>
        </div>
      </div>
    </template>
    <template v-slot:content>
      <DataTable
        dataKey="id"
        responsiveLayout="stack"
        :value="results"
        :scrollHeight="$isLargeScreen.value ? '500px' : undefined"
        :scrollable="$isLargeScreen.value"
        @sort="onSort($event)"
      >
        <template #header>
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText
              v-model="filters.term"
              placeholder="Buscar bovinos"
              @input="search()"
            />
          </span>
        </template>
        <Column
          header="Brinco"
          field="earring"
          :sortable="true"
          style="min-width: 100px"
          alignFrozen="left"
          :frozen="true"
        >
          <template #body="{ data: { earring, id } }">
            <router-link
              :to="{ name: 'bovine.edit', params: { id } }"
              target="_blank"
              style="text-decoration: none"
            >
              {{ earring }}
            </router-link>
          </template>
        </Column>
        <Column
          header="Tipo"
          field="type.name"
          :sortable="true"
          style="min-width: 100px"
          alignFrozen="left"
          :frozen="true"
        >
          <template #body="{ data: { type, id } }">
            <router-link
              :to="{ name: 'bovine.edit', params: { id } }"
              target="_blank"
              style="text-decoration: none"
            >
              <BovineType :type="type" small />
            </router-link>
          </template>
        </Column>
        <Column
          header="Data de nascimento"
          field="birthDate"
          :sortable="true"
          style="min-width: 120px"
        >
          <template #body="slotProps">
            {{
              slotProps.data.birthDate
                ? $dayjs(slotProps.data.birthDate).format('DD/MM/YYYY')
                : '-'
            }}
          </template>
        </Column>
        <Column
          header="Status"
          field="status.name"
          :sortable="true"
          style="min-width: 120px"
        >
          <template #body="props">
            <span v-if="props.data.editing">
              <AutocompleteApi
                v-model:value="props.data.status"
                v-model:valueId="props.data.statusId"
                :search="fetchStatus"
                @change="onStatusChange(props.data, $event)"
              ></AutocompleteApi>
            </span>
            <span v-else>
              {{ props.data?.status?.name || '-' }}
            </span>
          </template>
        </Column>
        <Column
          header="Previsão de parto"
          field="childBirth"
          :sortable="true"
          style="min-width: 150px"
          alignFrozen="right"
          :frozen="true"
        >
          <template #body="{ data }">
            <span v-if="data.editing">
              <Calendar
                v-model="data.childBirth"
                dateFormat="dd/mm/yy"
                showIcon
                :monthNavigator="true"
                :yearNavigator="true"
                :yearRange="dateRange"
                :minDate="dateStart"
                :showOnFocus="false"
                v-mask="'##/##/####'"
              />
            </span>
            <span v-else>
              {{ $formatDate(data.childBirth) || '-' }}
            </span>
          </template>
        </Column>
        <Column
          header="Peso"
          field="weight"
          :sortable="true"
          style="min-width: 120px"
        >
          <template #body="{ data }">
            <span v-if="data.editing">
              <InputNumber
                v-model="data.weight"
                mode="decimal"
                :minFractionDigits="2"
              />
            </span>
            <span v-else>
              {{ data.weight ? $toDecimal(data.weight) : '-' }}
            </span>
          </template>
        </Column>
        <Column
          header="Dt. Execução do Serviço"
          field="serviceBovine.dateExecuted"
          :sortable="true"
          style="min-width: 150px"
          alignFrozen="right"
          :frozen="true"
        >
          <template #body="{ data }">
            <span v-if="data.editing">
              <Calendar
                v-model="data.serviceBovine.dateExecuted"
                dateFormat="dd/mm/yy"
                showIcon
                :monthNavigator="true"
                :yearNavigator="true"
                :yearRange="dateRange"
                :minDate="dateStart"
                :showOnFocus="false"
                v-mask="'##/##/####'"
              />
            </span>
            <span v-else>
              {{ $formatDate(data.serviceBovine.dateExecuted) || '-' }}
            </span>
          </template>
        </Column>
        <Column
          header="Inseminação"
          :sortable="true"
          style="min-width: 150px"
          alignFrozen="right"
          :frozen="true"
        >
          <template #body="{ data }">
            <span v-if="data.editing">
              <AutoComplete
                v-model="data.serviceBovine.bullType"
                :suggestions="bullTypes"
                @complete="fetchBullTypes($event)"
                field="name"
                dropdown
              ></AutoComplete>
            </span>
            <span v-else>
              {{
                data.serviceBovine.bullType?.name ||
                data.serviceBovine.bullType ||
                '-'
              }}
            </span>
          </template>
        </Column>
        <Column
          header="Concluído"
          style="min-width: 100px"
          alignFrozen="right"
          :frozen="true"
        >
          <template #body="{ data }">
            <InputSwitch
              :modelValue="data.serviceBovine.done || done"
              @update:modelValue="onToggleDone({ data })"
            ></InputSwitch>
          </template>
        </Column>
        <Column
          header="Ações"
          style="min-width: 100px"
          alignFrozen="right"
          :frozen="true"
        >
          <template #body="props">
            <span v-if="!props.data.editing">
              <Button
                type="button"
                icon="pi pi-pencil"
                class="p-button-text p-button-secondary p-mr-1"
                @click="initEdit(props)"
              ></Button>
              <Button
                type="button"
                icon="pi pi-trash"
                class="p-button-text p-button-secondary"
                @click="removeBovine(props)"
              ></Button>
            </span>
            <span v-else>
              <Button
                type="button"
                icon="pi pi-check"
                class="p-button-text p-button-secondary p-mr-1"
                @click="saveEdit(props)"
              ></Button>
              <Button
                type="button"
                icon="pi pi-times"
                class="p-button-text p-button-secondary"
                @click="cancelEdit(props)"
              ></Button>
            </span>
          </template>
        </Column>
      </DataTable>
      <Paginator
        :first="query.pageSize * pagination.current - 1"
        :rows="query.pageSize"
        :totalRecords="pagination.total"
        @page="changeListPage({ page: $event.page + 1 })"
      ></Paginator>
      <BovineSelector
        v-model:visible="selectorVisible"
        @select="onBovineSelect($event)"
        :defaultQuery="
          serviceId
            ? { excludeServiceId: serviceId, available: true, farmId }
            : { available: true, farmId }
        "
        :showSelectAll="true"
        :apiOptions="getApiOptions()"
      ></BovineSelector>
    </template>
  </Card>
</template>

<script>
import debounce from 'debounce';
import { BovineApi, BullApi, ServiceApi } from '../../../services/api';
import BovineType from '../../../components/BovineType';
import BovineSelector from '../../../components/BovineSelector';
import AutocompleteApi from '../../../components/AutocompleteApi.vue';

export default {
  name: 'ServiceBovineCard',
  props: {
    farmId: Number,
    bovines: Array,
    serviceId: Number,
    done: Boolean,
    bovineCount: Number,
    dateStart: Date,
    customer: Object,
  },
  emits: ['save', 'update:bovineCount'],
  components: {
    BovineType,
    BovineSelector,
    AutocompleteApi,
  },
  async created() {
    await this.fetchList();
  },
  data() {
    return {
      filters: { term: '' },
      selectorVisible: false,
      results: [],
      bullTypes: [],
      loading: false,
      query: {
        page: 1,
        pageSize: 20,
        order: 'desc',
        orderField: 'serviceBovineCreatedAt',
      },
      pagination: {
        total: 0,
        current: 1,
        pages: 1,
      },
      dateRange: `${this.$dayjs()
        .subtract(10, 'y')
        .format('YYYY')}:${this.$dayjs().add(10, 'y').format('YYYY')}`,
    };
  },
  watch: {
    serviceId() {
      this.fetchList();
    },
  },
  methods: {
    getApiOptions() {
      return {
        accessToken: this.customer?.accessToken,
      };
    },
    initEdit(props) {
      props.oldData = JSON.parse(JSON.stringify(props.data));
      props.data.editing = true;
    },
    cancelEdit(props) {
      props.data.editing = false;
      props.data = Object.assign(props.data, props.oldData);
      if (props.data.serviceBovine.dateExecuted) {
        props.data.serviceBovine.dateExecuted = new Date(
          props.data.serviceBovine.dateExecuted
        );
      }
      props.oldData = null;
    },
    async saveEdit(props) {
      props.data.editing = false;
      props.oldData = null;
      if (!props.data?.serviceBovine?.id) {
        return;
      }
      const updateServiceData = {
        ...props.data.serviceBovine,
        bullType:
          props.data.serviceBovine.bullType?.name ||
          props.data.serviceBovine.bullType ||
          undefined,
      };
      const updateService = ServiceApi.updateBovine(
        props.data.serviceBovine.id,
        updateServiceData,
        this.getApiOptions()
      );
      const updateBovineData = {
        weight: props.data.weight,
        statusId: props.data.statusId,
        childBirth: props.data.childBirth,
      };
      const updateBovine = BovineApi.patch(
        props.data.id,
        updateBovineData,
        this.getApiOptions()
      );
      await Promise.all([updateService, updateBovine]);
      this.$toast.add({
        severity: 'success',
        summary: 'Atualizado com sucesso!',
        detail: 'O bovino foi atualizado com sucesso.',
        life: 3000,
      });
    },
    async fetchBullTypes(event) {
      const result = await BullApi.fetch(
        {
          name: event.query,
          orderField: 'name',
          order: 'asc',
        },
        this.getApiOptions()
      );
      this.bullTypes = result.results;
    },
    fetchStatus(query) {
      return BovineApi.fetchStatus(query, this.getApiOptions());
    },
    async onSort(event) {
      this.query.order = event.sortOrder === 1 ? 'asc' : 'desc';
      this.query.orderField = event.sortField;
      this.fetchList();
    },
    async fetchList() {
      if (!this.serviceId) {
        return;
      }
      try {
        this.loading = true;
        const bovinesResponse = await ServiceApi.fetchBovines(
          this.serviceId,
          this.query,
          this.getApiOptions()
        );
        this.results = bovinesResponse.results.map((result) => {
          if (result.serviceBovine.dateExecuted) {
            result.serviceBovine.dateExecuted = new Date(
              result.serviceBovine.dateExecuted
            );
          }
          return result;
        });
        this.pagination = bovinesResponse.pagination;
        this.$emit('update:bovineCount', this.pagination.total);
      } finally {
        this.loading = false;
      }
    },
    async changeListPage({ page }) {
      this.query.page = page;
      await this.fetchList();
    },
    search: debounce(async function search() {
      this.query.term = this.filters.term;
      this.fetchList();
    }, 500),
    async onToggleDone({ data }) {
      if (!data?.serviceBovine?.id) {
        return;
      }
      if (this.done) {
        data.serviceBovine.done = false;
        this.$emit('update:done', false);
      } else {
        data.serviceBovine.done = !data.serviceBovine.done;
      }
      await ServiceApi.updateBovine(
        data?.serviceBovine?.id,
        {
          done: data.serviceBovine.done,
        },
        this.getApiOptions()
      );
    },
    async onBovineSelect({ values }) {
      const bovines = values.map(
        ({ id: bovineId, bullTypeId, dateExecuted, done }) => ({
          bovineId,
          done: !!done,
          bullTypeId,
          dateExecuted: dateExecuted || this.dateStart,
        })
      );
      if (this.serviceId) {
        await ServiceApi.patch(
          this.serviceId,
          { bovines },
          this.getApiOptions()
        );
        await this.fetchList();
      } else {
        this.$emit('update:bovines', bovines);
        this.$emit('save');
      }
      this.$emit('update:done', false);
    },
    async removeBovine({ data }) {
      if (!data?.serviceBovine?.id) {
        return;
      }
      await ServiceApi.deleteBovine(
        data?.serviceBovine?.id,
        this.getApiOptions()
      );
      await this.fetchList();
    },
    onStatusChange(data, event) {
      if (event?.id === 2) {
        data.childBirth = this.$dayjs().add(9, 'month').toDate();
      } else {
        data.childBirth = null;
      }
    },
  },
};
</script>

<style scoped>
div >>> .p-inputtext {
  width: 100%;
}

.import-btn {
  color: var(--green-700) !important;
}
</style>
